/**
 * Section: Site footer
 * ------------------------------------------------------------------------------
 * Contains highly coupled scripts to control site footer components.
 *
 * @namespace siteFooter
 */
import {register} from '@shopify/theme-sections';

import cssClasses from '~/helpers/cssClasses';
import Accordion from '~/components/accordion';
import breakpoints from '~/helpers/breakpoints';
import Modal from '~/components/modal';
import {debounce, on} from '~/helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  footerToggle: '#footerAccordion',
  redirectModalToggle: '[js-site-footer="redirectModalToggle"]',
  submitBtn: '[js-newsletter-callout="submit"]',
  consentCheckbox: '[js-newsletter-callout="consent"]',
};

/**
 * Register the `site-footer` section type.
 */
register('site-footer', {

  /**
 * Component selectors.
 */
  setNodeSelectors() {
    this.redirectModalToggle = document.querySelector(selectors.redirectModalToggle);
    this.submitBtn = document.querySelector(selectors.submitBtn);
    this.consentCheckbox = document.querySelector(selectors.consentCheckbox);
  },

  /**
   * Set event listeners.
   */
  setEventListeners() {
    on('change', this.consentCheckbox, (event) => {
      if (event.target.checked) {
        this.submitBtn.removeAttribute('disabled');
      } else {
        this.submitBtn.setAttribute('disabled', true);
      }
    });
    on('resize', debounce(this.initAccordion, 250));
  },

  /**
   * Initialise footer accordion.
   */
  initAccordion() {
    const footerToggle = document.querySelector(selectors.footerToggle);

    if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
      footerToggle.classList.remove(cssClasses.active);
      return;
    }

    if (footerToggle.classList.contains(cssClasses.active)) {
      return;
    }

    footerToggle.classList.add(cssClasses.active);
    Accordion(selectors.footerToggle, {
      singleOpen: true,
      expandInitial: false,
    }).init();
  },

  /**
 * Set redirect modal toggle events.
 */
  setRedirectModalToggle() {
    const modal = Modal({
      id: 'redirect-modal',
    });

    on('click', this.redirectModalToggle, (event) => {
      event.preventDefault();
      modal.show('redirect-modal');
      document.body.classList.add('modal-settings');
    });
  },

  /**
   * Callback function when the section is loaded in via the  `sections.load()`
   * function or by the Theme Editor `shopify:section:unload` event.
   */
  onLoad() {
    this.setNodeSelectors();
    this.initAccordion();
    this.setEventListeners();
    this.setRedirectModalToggle();
  },
});
